import { Armchair, Handshake, Package, UserRound, Wrench } from "lucide-react"

import type { Linea } from "@/componentes/ordenes/tipos"
import { Insignia, type Tono } from "@/componentes/ui/Insignia"

export type Naturaleza = "SUELDO" | "SERVICIO" | "MATERIAL" | "CONSUMIBLE" | "BIEN"

/**
 * Las CINCO cosas que puede ser un concepto, cada una con su palabra, su icono y su
 * tono.
 *
 * ⚠ **La quinta es el SUELDO**, y llegó porque las otras cuatro describen cosas —un
 * servicio contratado, material, un consumible, un bien— y lo que se le paga a una
 * persona de la casa no es ninguna: salía como «Servicio», que es lo que se le dice a
 * una empresa de fuera. Su marca la pone la NÓMINA, no quien redacta a mano.
 *
 * ⚠ **Las cuatro primeras se administran distinto, y hasta ahora se veían de dos colores.** Una
 * línea con artículo salía en azul —daba igual tóner que fotocopiadora— y una sin él en
 * gris, así que un servicio y un material de uso inmediato eran la misma insignia. En
 * una factura de ocho conceptos eso obliga a abrir uno por uno para saber cuál entra al
 * depósito, cuál se deprecia y cuál se acabó al pagarlo.
 *
 * ⚠ **El color NUNCA va solo** (WCAG 1.4.1): cada insignia lleva su palabra y su icono,
 * y el tono es el refuerzo. Los tonos salen de los seis del sistema, no de un color
 * suelto — `exito` aquí no significa «bien hecho», significa «bien de la agencia», y por
 * eso la palabra manda.
 */
export const NATURALEZAS: Record<
  Naturaleza,
  { etiqueta: string; tono: Tono; Icono: typeof Wrench }
> = {
  SUELDO: { etiqueta: "Sueldo", tono: "aviso", Icono: UserRound },
  SERVICIO: { etiqueta: "Servicio", tono: "neutro", Icono: Handshake },
  MATERIAL: { etiqueta: "Material de uso inmediato", tono: "primario", Icono: Wrench },
  CONSUMIBLE: { etiqueta: "Consumible", tono: "info", Icono: Package },
  BIEN: { etiqueta: "Bien", tono: "exito", Icono: Armchair },
}

/** Qué es esta línea. El sueldo manda sobre todo; después el artículo; sin él,
 *  `uso_inmediato` separa material de servicio. */
export function naturalezaDe(linea: Linea): Naturaleza {
  // Primero el sueldo: lo que se le paga a una persona no es ninguna de las otras
  // cuatro, que describen cosas. Un sueldo nunca lleva artículo ni es material —hay
  // dos CHECK que lo impiden—, así que el orden no oculta nada.
  if (linea.es_sueldo) return "SUELDO"
  if (linea.articulo_id !== null) {
    return linea.articulo_clase === "BIEN" ? "BIEN" : "CONSUMIBLE"
  }
  return linea.uso_inmediato ? "MATERIAL" : "SERVICIO"
}

/**
 * La insignia de un concepto: qué es, no cómo se llama.
 *
 * ⚠ **El nombre del artículo ya está en la descripción de la línea** —se copia de él al
 * crearla—, así que repetirlo aquí era decir dos veces lo mismo y gastar el único sitio
 * donde cabía decir qué es. Si el artículo se renombró después, se añade detrás en
 * gris: la orden sigue diciendo lo que decía, y el depósito, lo que dice hoy.
 */
export function InsigniaNaturaleza({ linea }: { linea: Linea }) {
  const { etiqueta, tono, Icono } = NATURALEZAS[naturalezaDe(linea)]
  const renombrado =
    linea.articulo && linea.articulo.trim() !== linea.descripcion.trim() ? linea.articulo : null
  return (
    <>
      <Insignia tono={tono} icono={<Icono size={12} strokeWidth={2} aria-hidden="true" />}>
        {etiqueta}
      </Insignia>
      {renombrado && (
        <span className="truncate" style={{ color: "var(--fg-muted)" }}>
          {renombrado}
        </span>
      )}
    </>
  )
}
